<template>
  <div class="tabs">
    <div
      :class="['tabs__nav', 'tabs__nav--back', {
        'tabs__nav--active': backNavActive,
      }]"
      @click="goBack"
    >
      <zg-icon
        class="icon"
        icon="arrow-back"
        size="small"
      />
    </div>

    <div
      :class="['tabs__nav', 'tabs__nav--forward', {
        'tabs__nav--active': forwardNavActive,
      }]"
      @click="goForward"
    >
      <zg-icon
        class="icon"
        icon="arrow-forward"
        size="small"
      />
    </div>

    <div
      ref="items"
      class="tabs__items"
      :style="{ left: `${leftPosition}px` }"
    >
      <button
        v-for="(tab, index) in tabs"
        :key="index"
        :ref="`tab-${index}`"
        :class="['tabs__tab', {
          'tabs__tab--selected': index === selectedTab
        }]"
        type="button"
        @click="setSelectedTab(tab.product, index)"
      >
        <zg-label
          v-if="tab.tag"
          :text="tab.tag"
          class="tag"
        />

        <zg-product-icon
          v-if="tab.product"
          :product="tab.product"
          class="icon"
        />

        <span>
          {{ tab.text }}
        </span>
      </button>
    </div>
  </div>
</template>

<script>
  import ZgIcon from '../ZgIcon/ZgIcon'
  import ZgLabel from '../ZgLabel/ZgLabel'
  import ZgProductIcon from '../ZgProductIcon/ZgProductIcon'

  export default {
    name: 'Tabs',
    components: {
      ZgLabel,
      ZgIcon,
      ZgProductIcon
    },
    props: {
      tabs: {
        type: Array,
        required: false,
        default: null
      },
      selectedTab: {
        type: Number,
        required: false,
        default: null
      }
    },
    data: () => ({
      forwardNavActive: true,
      activeTab: 0,
      leftPosition: 0
    }),
    computed: {
      backNavActive () {
        return this.activeTab !== 0
      }
    },
    mounted () {
      window.addEventListener('resize', this.setLeftPosition)
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.setLeftPosition)
    },
    methods: {
      randomString () {
        return (Math.random() + 1).toString(36).substring(7)
      },
      setSelectedTab (product, index) {
        this.$emit('changed-tab', { product, index })
      },
      goForward () {
        if (this.activeTab >= this.tabs?.length) return
        this.activeTab++

        this.setLeftPosition()
      },
      goBack () {
        if (this.activeTab === 0) return
        this.activeTab--

        this.setLeftPosition()
      },
      setLeftPosition (e) {
        const itemsWidth = this.$refs.items?.offsetWidth - (96 * 2)
        const itemsOffset = this.$refs.items?.offsetWidth + (96 * 2)
        const offsetActiveTab = this.$refs[`tab-${this.activeTab}`]?.[0]?.offsetLeft
        const targetWidth = e?.currentTarget?.innerWidth

        const lastTab = this.$refs[`tab-${this.tabs?.length - 1}`]
        const offsetLastTab = lastTab?.[0]?.offsetLeft
        const widthLastTab = lastTab?.[0]?.offsetWidth

        this.forwardNavActive = ((offsetLastTab + widthLastTab) - offsetActiveTab) > itemsWidth
        if (!this.forwardNavActive) {
          if (targetWidth && targetWidth > itemsOffset) {
            this.leftPosition = 0
          } else {
            this.leftPosition = -offsetLastTab + (itemsWidth - widthLastTab)
          }
        } else {
          this.leftPosition = -offsetActiveTab
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  $max-width: rem(1080px);
  $content-desktop-breakpoint: 1282px;

  .tabs {
    position: relative;
    width: 100%;
    height: rem(96px);
    display: flex;
    align-items: flex-end;
    overflow-x: auto;
    overflow-y: visible;

    @include mq(medium) {
      height: rem(96px);
      overflow-x: clip;
    }

    &__items {
      position: relative;
      width: 100%;
      max-width: $max-width;
      display: flex;
      flex-direction: row;
      gap: rem(8px);
      margin: 0 auto;
      z-index: 5;
      transition: all 0.7s ease;

      @include mq(medium) {
        position: absolute;
        top: 0;
        gap: rem(8px);
        margin: 0 rem(96px);
      }

      @include mq($content-desktop-breakpoint) {
        position: relative;
        top: unset;
        gap: rem(16px);
        margin: 0 auto;
      }
    }

    &__tab {
      @include type-title-xs;

      position: relative;
      display: flex;
      align-content: center;
      align-items: center;
      flex-grow: 1;
      flex-shrink: 0;
      gap: rem(8px);
      height: rem(80px);
      color: color(black);
      background-color: color(tabs-inactive);
      border: 4px solid color(tabs-inactive);
      border-radius: rem(32px) rem(32px) 0 0;
      outline: none;
      padding: rem(16px);
      margin: 0;
      transition: all 0.3s ease;
      cursor: pointer;

      @include mq(medium) {
        height: rem(144px);
        padding: rem(16px) rem(24px) (rem(16px) + rem(48px));
        border-radius: rem(48px) rem(48px) 0 0;
      }

      @include mq($content-desktop-breakpoint) {
        height: rem(96px);
        padding: rem(16px) rem(24px);
      }

      &:hover {
        background-color: color(brand-300);
        border-color: color(brand-300);
      }

      &:focus {
        background-color: color(brand-100);
        border-color: color(brand-300);
        box-shadow: box-shadow(focus);
      }

      &:focus:not(:focus-visible) {
        background-color: color(brand-100);
        border-color: color(brand-100);
        box-shadow: none;
      }

      .tag {
        position: absolute;
        top: - rem(4px);
        right: rhythm(xsmall);
        transform: translateY(-50%);
      }

      .icon {
        width: rem(48px);
        height: rem(48px);

        @include mq($content-desktop-breakpoint) {
          width: rem(64px);
          height: rem(64px);
        }
      }

      &--selected {
        background-color: color(tabs-active);
        border-color: color(tabs-active);

        &:hover,
        &:focus,
        &:focus:not(:focus-visible) {
          background-color: color(tabs-active);
          border-color: color(tabs-active);
          box-shadow: none;
        }
      }
    }

    &__nav {
      display: none;

      @include mq(medium) {
        position: absolute;
        top: 50%;
        height: rhythm(8);
        width: rhythm(8);
        display: flex;
        align-items: center;
        justify-content: center;
        background: color(primary-500);
        padding: 1rem;
        border-radius: 100%;
        transform: translateY(-50%);
        cursor: pointer;
        opacity: 0.5;
        z-index: 6;
        pointer-events: none;

        &--active {
          opacity: 1;
          pointer-events: unset;
        }

        &--back {
          left: rem(16px);
        }

        &--forward {
          right: rem(16px);
        }
      }

      @include mq($content-desktop-breakpoint) {
        display: none;
      }
    }
  }
</style>
